import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { errorMessages } from '~/constants/errorMessages.ts';
import type { TimerRefHandle } from '~/components/SignatureVerification/DiiaQRCode.tsx';
import { useFilesSigning } from '~/modules/guarantees/form/application/shared/filesSigning/useFilesSigning.ts';
import { useApplicationRequestFeedback } from '~/modules/guarantees/form/application/shared/hooks/useApplicationRequestFeedback.ts';
import { useGuaranteesPdfPreview } from '~/modules/guarantees/form/application/shared/hooks/useGuaranteesPdfPreview.ts';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  buildGuaranteesApplicationPath,
  buildGuaranteesApplicationStep2Path,
  buildGuaranteesCounterpartyPath,
  getStringValue,
  isGuaranteesClientInitialSigningStatus,
  isGuaranteesClientStep3Status,
} from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
} from '~/store/guarantees/selectors.ts';
import {
  downloadGuaranteesClientDocument,
  fetchGuaranteesApplication,
  fetchGuaranteesApplicationFiles,
  submitGuaranteesApprovalLetterByClient,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
  type TFilesSigningSaveSignedDocumentsResponse,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import {
  getGuaranteesApplicationIdentity,
  getGuaranteesApplicationDisplayNumber,
  normalizeGuaranteesApplicationStatus,
} from '~/store/guarantees/utils.ts';
import { selectIIT } from '~/store/iit/selectors.ts';
import type { AppDispatch } from '~/store/store.ts';
import type { TGuaranteesModalSuccessState } from '../../types.ts';
import { CLIENT_APPROVAL_LETTER_SIGN_TEXT } from './constants.ts';
import {
  buildClientApprovalLetterSigningDocuments,
  getClientApprovalLetterFile,
  getClientApprovalLetterPreviewFileId,
} from './utils.ts';

type TPreviewDocument = {
  data: Blob;
  contentType?: string;
};

export const useClientApprovalLetterSignPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const { isLoading: isIITLoading } = useSelector(selectIIT) || {};
  const timerRef = useRef<TimerRefHandle>(null);
  const [applicationFiles, setApplicationFiles] = useState<TGuaranteesApplicationFile[]>([]);
  const [isApplicationFilesLoading, setIsApplicationFilesLoading] = useState(false);
  const [hasApplicationFilesLoaded, setHasApplicationFilesLoaded] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<TPreviewDocument | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSuccessRedirectLocked, setIsSuccessRedirectLocked] = useState(false);
  const [deepLink, setDeepLink] = useState('');
  const loadedApplicationId = getGuaranteesApplicationIdentity(application);
  const isLoadedApplicationCurrent = loadedApplicationId === applicationId;
  const currentRouteApplication = isLoadedApplicationCurrent ? application : null;
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const {
    applicationNumber,
    isTemporaryApplicationNumber,
  } = getGuaranteesApplicationDisplayNumber(currentRouteApplication);
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isStepLoading = isApplicationLoading || isApplicationFilesLoading;
  const approvalLetterFile = useMemo(
    () => getClientApprovalLetterFile(applicationFiles),
    [applicationFiles]
  );
  const { fileIds, documents } = useMemo(
    () => buildClientApprovalLetterSigningDocuments(approvalLetterFile),
    [approvalLetterFile]
  );
  const redirectPath = !contragentId
    ? '/guarantees/get-started'
    : currentRouteApplication && isGuaranteesClientInitialSigningStatus(normalizedStatus)
      ? buildGuaranteesApplicationStep2Path(effectiveApplicationId, contragentId, false)
      : currentRouteApplication && !isGuaranteesClientStep3Status(normalizedStatus)
        ? buildGuaranteesApplicationPath(effectiveApplicationId, contragentId, false)
        : '';
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const pdfPreview = useGuaranteesPdfPreview({
    contentType: previewDocument?.contentType,
    documentBlob: previewDocument?.data,
    missingPdfText: CLIENT_APPROVAL_LETTER_SIGN_TEXT.missingFileMessage,
    setModalAlert,
  });
  const isDocumentLoading = isApplicationFilesLoading || isPreviewLoading || pdfPreview.isDocumentLoading;
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberTooltip: isTemporaryApplicationNumber
        ? guaranteesClientText.temporaryApplicationNumberTooltip
        : undefined,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate(buildGuaranteesCounterpartyPath(contragentId, false)),
      statusConfig,
    }),
    [
      applicationNumber,
      contragentId,
      effectiveApplicationId,
      isTemporaryApplicationNumber,
      navigate,
      statusConfig,
    ]
  );

  const navigateToApplications = useCallback(() => {
    setIsSuccessRedirectLocked(false);
    navigate(buildGuaranteesCounterpartyPath(contragentId, false), { replace: true });
  }, [contragentId, navigate]);

  const setSigningSuccessModal = useCallback(
    (value: TGuaranteesModalSuccessState) => setModalSuccess(value),
    [setModalSuccess]
  );

  const getActualApplicationVersion = useCallback(
    async (saveResponse?: TFilesSigningSaveSignedDocumentsResponse) => {
      if (typeof saveResponse?.version === 'number') {
        return saveResponse.version;
      }

      if (!contragentId || !applicationId || applicationId === 'new') {
        return null;
      }

      const refreshedApplication = await dispatch(
        fetchGuaranteesApplication({ contragentId, applicationId })
      ).unwrap();

      return typeof refreshedApplication?.version === 'number'
        ? refreshedApplication.version
        : null;
    },
    [applicationId, contragentId, dispatch]
  );

  const onSigningSuccess = useCallback(
    async (saveResponse?: TFilesSigningSaveSignedDocumentsResponse) => {
      setIsSignatureModalOpen(false);
      setIsSuccessRedirectLocked(true);

      let version: number | null = null;

      try {
        version = await getActualApplicationVersion(saveResponse);
      } catch (error) {
        setIsSuccessRedirectLocked(false);
        showGuaranteesRequestError(error);

        return;
      }

      if (
        !effectiveApplicationId ||
        effectiveApplicationId === 'new' ||
        typeof version !== 'number'
      ) {
        setIsSuccessRedirectLocked(false);
        showGuaranteesRequestError({ message: errorMessages.SERVICE_UNAVAILABLE });

        return;
      }

      try {
        await dispatch(
          submitGuaranteesApprovalLetterByClient({
            applicationId: effectiveApplicationId,
            version,
          })
        ).unwrap();
      } catch (error) {
        setIsSuccessRedirectLocked(false);
        showGuaranteesRequestError(error);

        return;
      }

      setSigningSuccessModal({
        text: CLIENT_APPROVAL_LETTER_SIGN_TEXT.signSuccessMessage,
        buttonValue: guaranteesClientText.backToApplicationsButton,
        onClick: navigateToApplications,
        onClose: navigateToApplications,
      });
    },
    [
      dispatch,
      effectiveApplicationId,
      getActualApplicationVersion,
      navigateToApplications,
      setSigningSuccessModal,
      showGuaranteesRequestError,
    ]
  );

  const onSigningError = useCallback(
    (message: string) => {
      setIsSignatureModalOpen(false);
      setModalAlert({ text: message });
    },
    [setModalAlert]
  );

  const { handleIITSignature, onIITSign, handleDiiaSignature, resetSigningSession } =
    useFilesSigning({
      fileIds,
      applicationId: effectiveApplicationId,
      onSuccess: onSigningSuccess,
      onError: onSigningError,
      setDeepLink,
      timerRef,
    });

  const handleSign = () => {
    if (!fileIds.length) {
      setModalAlert({ text: CLIENT_APPROVAL_LETTER_SIGN_TEXT.missingFileMessage });

      return;
    }

    setIsSignatureModalOpen(true);
  };

  const handleCloseSignatureModal = () => {
    setIsSignatureModalOpen(false);
    resetSigningSession();
  };

  const handleTimeUp = () => {
    resetSigningSession();
    showGuaranteesRequestError({ message: errorMessages.SIGNATURE_QR_CODE_TIME_OUT });
    setIsSignatureModalOpen(false);
  };

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    if (!effectiveApplicationId || effectiveApplicationId === 'new') {
      setApplicationFiles([]);
      setHasApplicationFilesLoaded(true);

      return;
    }

    let isCancelled = false;

    const loadApplicationFiles = async () => {
      setIsApplicationFilesLoading(true);

      try {
        const nextApplicationFiles = await dispatch(
          fetchGuaranteesApplicationFiles({ applicationId: effectiveApplicationId })
        ).unwrap();

        if (!isCancelled) {
          setApplicationFiles(nextApplicationFiles);
          setHasApplicationFilesLoaded(true);
        }
      } catch (error) {
        if (!isCancelled) {
          setApplicationFiles([]);
          setHasApplicationFilesLoaded(true);
          showGuaranteesRequestError(error);
        }
      } finally {
        if (!isCancelled) {
          setIsApplicationFilesLoading(false);
        }
      }
    };

    void loadApplicationFiles();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, effectiveApplicationId, showGuaranteesRequestError]);

  useEffect(() => {
    const previewFileId = getClientApprovalLetterPreviewFileId(approvalLetterFile);

    if (isApplicationFilesLoading || !hasApplicationFilesLoaded) {
      return;
    }

    if (!previewFileId) {
      setPreviewDocument(null);
      setModalAlert({ text: CLIENT_APPROVAL_LETTER_SIGN_TEXT.missingFileMessage });

      return;
    }

    let isCancelled = false;

    const loadPreview = async () => {
      setPreviewDocument(null);
      setIsPreviewLoading(true);

      try {
        const downloadedDocument = await dispatch(
          downloadGuaranteesClientDocument({
            id: previewFileId,
            isAttached: false,
          })
        ).unwrap();

        if (!isCancelled) {
          setPreviewDocument({
            data: downloadedDocument.data,
            contentType: downloadedDocument.contentType,
          });
        }
      } catch (error) {
        if (!isCancelled) {
          setPreviewDocument(null);
          showGuaranteesRequestError(error);
        }
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      isCancelled = true;
    };
  }, [
    approvalLetterFile,
    dispatch,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    setModalAlert,
    showGuaranteesRequestError,
  ]);

  return {
    applicationError,
    contragentId,
    deepLink,
    documents,
    effectiveApplicationId,
    headerProps,
    isDocumentLoading,
    isSignatureModalOpen,
    isSuccessRedirectLocked,
    isSigningLoading: isIITLoading,
    isStepLoading,
    modalAlert,
    modalSuccess,
    pdfUrl: pdfPreview.pdfUrl,
    redirectPath,
    timerRef,
    handleCloseSignatureModal,
    handleDiiaSignature,
    handleIITSignature,
    handleOpenPdf: pdfPreview.handleOpenPdf,
    handleSign,
    handleTimeUp,
    onIITSign,
    onModalClose: handleCloseAlertModal,
    onSuccessModalClose: handleCloseSuccessModal,
    showGuaranteesRequestError,
  };
};
